'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetIdentityCheck } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { FieldShellSize } from '@/components/ui/field-shell-size';
import { Icon } from '@/components/ui/icon';
import { CancelCircleIcon, CheckmarkCircle02Icon, Loading03Icon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { HANDLE_RULES, validateHandle as validateHandleRules } from '@/lib/validation/identity';
export type HandleStatus = 'idle' | 'pending' | 'unique' | 'taken' | 'invalid' | 'reserved';
const DEBOUNCE_MS = 400;
interface HandleInputProps {
  value: string;
  onChange: (value: string) => void;
  onStatusChange?: (status: HandleStatus) => void;
  /** The user's current handle — skip availability check if unchanged. */
  currentHandle?: string | null | undefined;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  size?: FieldShellSize | undefined;
}
function clientStatusFor(handle: string): HandleStatus {
  if (handle.length === 0) return 'idle';
  const result = validateHandleRules(handle);
  if (result.ok) return 'idle';
  return result.error === 'reserved' ? 'reserved' : 'invalid';
}
export function HandleInput(t0) {
  const $ = _c(46);
  const {
    value,
    onChange,
    onStatusChange,
    currentHandle,
    required: t1,
    disabled: t2,
    id: t3,
    size
  } = t0;
  const required = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const id = t3 === undefined ? "handle-input" : t3;
  const t = useTranslations("profile.handle");
  let t4;
  if ($[0] !== value) {
    t4 = clientStatusFor(value);
    $[0] = value;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const clientStatus = t4;
  const matchesCurrent = currentHandle !== null && currentHandle !== undefined && value === currentHandle;
  const [debounced, setDebounced] = useState(value);
  let t5;
  let t6;
  if ($[2] !== value) {
    t5 = () => {
      const timer = setTimeout(() => setDebounced(value), DEBOUNCE_MS);
      return () => clearTimeout(timer);
    };
    t6 = [value];
    $[2] = value;
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  const shouldCheck = value.length > 0 && clientStatus === "idle" && !matchesCurrent && debounced === value;
  const t7 = shouldCheck ? debounced : "";
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["kind", "available"];
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t7) {
    t9 = {
      kind: "handle",
      handle: t7,
      fields: t8
    };
    $[6] = t7;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== shouldCheck) {
    t10 = {
      enabled: shouldCheck
    };
    $[8] = shouldCheck;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const {
    data,
    isPending: isChecking
  } = useGetIdentityCheck(t9, t10);
  let status;
  if ($[10] !== clientStatus || $[11] !== data || $[12] !== debounced || $[13] !== isChecking || $[14] !== matchesCurrent || $[15] !== value) {
    if (value.length === 0) {
      status = "idle";
    } else {
      if (clientStatus !== "idle") {
        status = clientStatus;
      } else {
        if (matchesCurrent) {
          status = "unique";
        } else {
          if (debounced !== value || isChecking) {
            status = "pending";
          } else {
            if (data !== null && data !== undefined && data.kind === "handle") {
              status = data.available === true ? "unique" : "taken";
            } else {
              status = "pending";
            }
          }
        }
      }
    }
    $[10] = clientStatus;
    $[11] = data;
    $[12] = debounced;
    $[13] = isChecking;
    $[14] = matchesCurrent;
    $[15] = value;
    $[16] = status;
  } else {
    status = $[16];
  }
  let t11;
  if ($[17] !== onStatusChange || $[18] !== status) {
    t11 = () => {
      onStatusChange?.(status);
    };
    $[17] = onStatusChange;
    $[18] = status;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== onStatusChange || $[21] !== status) {
    t12 = [status, onStatusChange];
    $[20] = onStatusChange;
    $[21] = status;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  if ($[23] !== onChange) {
    t13 = function handleChange(event) {
      const raw = event.target.value.replace(/^@/, "").toLowerCase().replace(/\s/g, "");
      onChange(raw);
    };
    $[23] = onChange;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const handleChange = t13;
  let t14;
  if ($[25] !== status || $[26] !== t) {
    t14 = status === "taken" ? t("taken") : status === "invalid" ? t("invalid", {
      min: HANDLE_RULES.MIN,
      max: HANDLE_RULES.MAX
    }) : status === "reserved" ? t("reserved") : undefined;
    $[25] = status;
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const errorMessage = t14;
  let t15;
  if ($[28] !== status) {
    t15 = status === "pending" ? <Icon icon={Loading03Icon} className="size-4 animate-spin text-foreground-secondary" /> : status === "unique" ? <Icon icon={CheckmarkCircle02Icon} className="size-4 text-green-500" /> : status === "taken" || status === "invalid" || status === "reserved" ? <Icon icon={CancelCircleIcon} className="size-4 text-feedback-error-content" /> : undefined;
    $[28] = status;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const rightIcon = t15;
  let t16;
  if ($[30] !== t) {
    t16 = t("label");
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t) {
    t17 = t("placeholder");
    $[32] = t;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="text-foreground-secondary select-none">@</span>;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== disabled || $[36] !== errorMessage || $[37] !== handleChange || $[38] !== id || $[39] !== required || $[40] !== rightIcon || $[41] !== size || $[42] !== t16 || $[43] !== t17 || $[44] !== value) {
    t19 = <InputField id={id} label={t16} required={required} type="text" value={value} onChange={handleChange} placeholder={t17} disabled={disabled} maxLength={HANDLE_RULES.MAX + 1} autoComplete="username" leftIcon={t18} rightIcon={rightIcon} error={errorMessage} size={size} />;
    $[35] = disabled;
    $[36] = errorMessage;
    $[37] = handleChange;
    $[38] = id;
    $[39] = required;
    $[40] = rightIcon;
    $[41] = size;
    $[42] = t16;
    $[43] = t17;
    $[44] = value;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
}